import { Shape, ShapeOptions } from "../Shape";
import { SVGParsedPathData } from "../svg";
import { YZCanvasContext } from "../canvas";
import { polar, toRadian } from "../../../math";

/**
 * CSS 坐标系
 * 0deg = 从下到上
 * 90deg = 从左到右
 *                   [0deg]            [45deg]
 *                      ⬆            ⬈
 *                      *         *
 *                      *     *
 *                      *  *
 *  [-90deg] ⬅ *  *  *  *  *  *  * ⮕  [90deg]
 *                      *
 *                      *
 *                      *
 *                      ⬇
 *                   [180deg]
 */

/**
 * canvas svg Vector 坐标系
 *                [-90° / 270°]
 *                  *
 *                  *
 *                  *
 *  [180°] *  *  *  *  *  *  *  [0°]
 *                  *
 *                  *
 *                  *
 *                [90°]
 */

export type RingShapeOptions = {
  half?: boolean; // 半圆 或 全圆
  lineWidth: number; // 圆环宽度
  radius?: number;
  width?: number;
  height?: number;
  forceCoordinate?: boolean;
  startDegree: number; // CSS 坐标系 half=true 时，左右两边比180°水平线多出的角度，范围 +90 ~ 0 ~ -90，设计图建议 +15°
  endDegree: number; // CSS 坐标系 half=true 时，左右两边比180°水平线多出的角度，范围 +90 ~ 0 ~ -90，设计图建议 +15°
  // bgColor?: CanvasGradient | string;
  // 渐变色
  startColor?: string;
  endColor?: string;
  percent?: number;
} & ShapeOptions;

// 圆环
export class RingShape extends Shape {
  public half: boolean;
  public lineWidth: number;

  public radius: number;

  public startDegree: number;
  public endDegree: number;

  public percent = 0;
  public percentDegree = 0;

  // public bgColor?: CanvasGradient | string;
  public startColor?: string;
  public endColor?: string;

  constructor(options: RingShapeOptions) {
    super(options);

    this.half = typeof options?.half == "boolean" ? options?.half : false;
    this.lineWidth = options.lineWidth;

    const width = options.width || 0;
    const height = options.height || 0;
    const extendDegree = (options.endDegree - options.startDegree - 180) / 2;

    // 圆半径
    if (options.radius) {
      this.radius = options.radius;
    } else if (this.half) {
      // 半圆
      const min = Math.min(width / 2, height) - this.lineWidth;
      this.radius = Math.max(0, min - min * Math.sin(toRadian(extendDegree)));
    } else {
      // 全圆
      this.radius = Math.max(
        0,
        Math.min(width / 2, height / 2) - this.lineWidth
      );
    }

    // 圆心 x 坐标 自动移动到画布中心
    if (!options.x) {
      this.x = width / 2;
    }

    // 圆心 y 坐标 自动移动到画布中心
    if (!options.y) {
      if (this.half) {
        this.y =
          height -
          this.lineWidth -
          this.radius * Math.sin(toRadian(extendDegree));
      } else {
        this.y = height / 2;
      }
    }

    if (options.forceCoordinate) {
      // clone
      this.startDegree = options.startDegree;
      this.endDegree = options.endDegree;
    } else {
      // css坐标系 切换成 画布坐标系
      this.startDegree = -90 + options.startDegree;
      this.endDegree = -90 + options.endDegree;
    }

    // this.bgColor = options.bgColor;
    this.startColor = options?.startColor;
    this.endColor = options?.endColor;
    this.setAttr({
      "stroke-linecap": "round",
      "stroke-width": String(this.lineWidth),
      stroke: this.startColor || "#000000" || "none"
    });

    // 默认 100%
    this.setPercent(options.percent || 1);
  }

  protected cloneShape(): RingShape {
    const shape = new RingShape({
      ...super.getBaseOptions(),
      half: this.half,
      lineWidth: this.lineWidth,
      radius: this.radius,
      forceCoordinate: true,
      startDegree: this.startDegree,
      endDegree: this.endDegree,
      startColor: this.startColor,
      endColor: this.endColor,
      percent: this.percent
    });
    return shape;
  }

  // 提供外部变更
  public setPercent(percent: number) {
    this.percent = Math.max(0, percent);
    this.percentDegree =
      this.startDegree + (this.endDegree - this.startDegree) * percent;
  }

  public enableGradient(startColor: string, endColor: string) {
    this.startColor = startColor;
    this.endColor = endColor;
    this.setAttr({
      stroke: this.startColor
    });
    // 在 toCanvasContext 或 toSVGPathData 里设置渐变
  }
  public disableGradient(startColor: string) {
    this.startColor = startColor;
    this.endColor = undefined;
    this.setAttr({
      stroke: this.startColor
    });
  }

  public toSVGPathData(): SVGParsedPathData[] {
    const { dx: fromX, dy: fromY } = polar(
      toRadian(this.startDegree),
      this.radius
    );
    const { dx: endX, dy: endY } = polar(
      toRadian(this.percentDegree),
      this.radius
    );
    return [
      ["m", fromX, fromY],
      [
        "a",
        this.radius,
        this.radius,
        0,
        Math.abs(this.percentDegree - this.startDegree) >= 180 ? 1 : 0,
        1,
        endX - fromX,
        endY - fromY
      ]
    ];
  }

  public toCanvasContext(ctx: YZCanvasContext) {
    if (this.startColor && this.endColor) {
      // canvas 支持渐变色
      let gradient;
      if (this.half) {
        const dX = this.radius - this.radius * Math.cos(Math.PI * this.percent);
        const dY = this.radius * Math.sin(Math.PI * this.percent);
        gradient = ctx.createLinearGradient(
          -this.radius,
          0,
          -this.radius + dX,
          -dY,
          {
            0: this.startColor,
            1: this.endColor
          }
        );
      } else {
        const dX = this.radius * Math.sin(2 * Math.PI * this.percent);
        const dY = this.radius * Math.cos(2 * Math.PI * this.percent);
        gradient = ctx.createLinearGradient(0, -this.radius, dX, -dY, {
          0: this.startColor,
          1: this.endColor
        });
      }
      this.setAttr({
        stroke: gradient
      });
    }
    ctx.beginPath();
    ctx.arc(
      0,
      0,
      this.radius,
      toRadian(this.startDegree),
      toRadian(this.percentDegree),
      false
    );
  }
}

export default RingShape;
